import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/apiClient';

const ContentContext = createContext();

// Parse CMS HTML content into structured sections
const parseCMSContent = (htmlContent) => {
  if (!htmlContent) return {};
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  const parsed = {
    _raw: htmlContent,
  };
  
  // Extract hero section
  const heroSection = doc.querySelector('.hero-section');
  if (heroSection) {
    parsed.hero = {
      tagline: heroSection.querySelector('.tagline')?.textContent || '',
      title: heroSection.querySelector('h1')?.textContent || '',
      description: heroSection.querySelector('p:not(.tagline)')?.textContent || '',
    };
  }
  
  // Extract features
  const features = doc.querySelectorAll('.feature');
  if (features.length > 0) {
    parsed.features = Array.from(features).map(f => ({
      title: f.querySelector('h3')?.textContent || '',
      description: f.querySelector('p')?.textContent || '',
    }));
  }
  
  // Extract CTA section
  const ctaSection = doc.querySelector('.cta-section');
  if (ctaSection) {
    parsed.cta = {
      title: ctaSection.querySelector('h2')?.textContent || '',
      description: ctaSection.querySelector('p')?.textContent || '',
    };
  }
  
  // Extract FAQ items
  const faqItems = doc.querySelectorAll('.faq-item');
  if (faqItems.length > 0) {
    parsed.faq = Array.from(faqItems).map(f => ({
      question: f.querySelector('h3')?.textContent || '',
      answer: f.querySelector('p')?.textContent || '',
    }));
  }
  
  // Extract blockquotes/testimonials
  const blockquotes = doc.querySelectorAll('blockquote');
  if (blockquotes.length > 0) {
    parsed.testimonials = Array.from(blockquotes).map(b => ({
      text: b.querySelector('p')?.textContent || '',
      name: b.querySelector('cite')?.textContent || '',
    }));
  }
  
  return parsed;
};

export function ContentProvider({ children }) {
  const [content, setContent] = useState({});
  const [rawContent, setRawContent] = useState({});
  const [globalElements, setGlobalElements] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllContent = async () => {
    try {
      // Fetch from SimpleCMS API (the active CMS system)
      const pages = ['home', 'about', 'contact', 'loyalty', 'menu'];
      const results = await Promise.all(
        pages.map(page => 
          api.get(`/simple-cms/pages/${page}`)
            .then(res => res.data)
            .catch(() => null)
        )
      );
      
      const contentMap = {};
      const rawMap = {};
      
      pages.forEach((page, i) => {
        if (results[i]) {
          // Store raw CMS data
          rawMap[page] = results[i];
          // Parse HTML content into structured data for backward compatibility
          contentMap[page] = {
            ...parseCMSContent(results[i].content),
            title: results[i].title,
            slug: results[i].slug,
            seo: results[i].seo || {},
            status: results[i].status,
          };
        }
      });
      
      // Fetch contact info from settings API for dynamic contact data
      try {
        const contactRes = await api.get('/contact-info');
        globalElements.contact = contactRes.data;
      } catch {
        // Use defaults
      }
      
      // Fetch navigation from CMS
      try {
        const navRes = await api.get('/cms/navigation');
        globalElements.navigation = navRes.data;
      } catch {
        // Use defaults
      }
      
      setContent(contentMap);
      setRawContent(rawMap);
      setGlobalElements(globalElements);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch content:', err);
      setLoading(false);
    }
  };

  // Get structured content for a page section
  const getContent = (page, section, fallback = {}) => {
    return content[page]?.[section] || fallback;
  };

  // Get raw HTML content for a page (for full CMS rendering)
  const getRawContent = (page) => {
    return rawContent[page]?.content || '';
  };

  // Get full page data including metadata
  const getPageData = (page) => {
    return rawContent[page] || null;
  };

  const getImage = (page, section, key, fallback = '') => {
    return content[page]?.[section]?.[key] || content[page]?.images?.[key] || fallback;
  };

  const getText = (page, section, key, fallback = '') => {
    const sectionContent = content[page]?.[section];
    if (!sectionContent) return fallback;
    
    if (typeof key === 'string' && key.includes('.')) {
      const keys = key.split('.');
      let value = sectionContent;
      for (const k of keys) {
        value = value?.[k];
      }
      return value || fallback;
    }
    
    return sectionContent[key] || fallback;
  };

  // Get SEO data for a page
  const getSEO = useCallback((page) => {
    return content[page]?.seo || rawContent[page]?.seo || {};
  }, [content, rawContent]);

  // Get global element data
  const getGlobal = useCallback((element, section, fallback = null) => {
    if (section) {
      return globalElements[element]?.[section] || fallback;
    }
    return globalElements[element] || fallback;
  }, [globalElements]);

  // Get footer data (for backward compatibility)
  const getFooter = useCallback(() => {
    return globalElements.footer || {};
  }, [globalElements]);

  // Get navigation data (for backward compatibility)
  const getNavigation = useCallback(() => {
    return globalElements.navigation || {};
  }, [globalElements]);

  // Get global settings (for backward compatibility)
  const getGlobalSettings = useCallback(() => {
    return globalElements.global || {};
  }, [globalElements]);

  const refreshContent = () => {
    fetchAllContent();
  };

  return (
    <ContentContext.Provider value={{
      content,
      rawContent,
      globalElements,
      loading,
      getContent,
      getRawContent,
      getPageData,
      getImage,
      getText,
      getSEO,
      getGlobal,
      getFooter,
      getNavigation,
      getGlobalSettings,
      refreshContent
    }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
}

// Custom hook for fetching a single CMS page with loading state
export function useCMSPage(slug) {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/simple-cms/pages/${slug}`);
        setPageData(res.data);
        setError(null);
      } catch (err) {
        console.error(`Error fetching ${slug} page:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPage();
  }, [slug]);

  return { pageData, loading, error, refetch: () => setLoading(true) };
}
